import { formatDate } from "@/lib/utils";
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Store,
  Calendar,
  User,
  Package,
  Layers,
  ScanBarcode,
  Truck,
} from "lucide-react";

export function TransferDetailsModal({ isOpen, onOpenChange, transfer }) {
  if (!transfer) return null;

  const formatAddress = (store) => {
    if (!store) return "-";
    const parts = [
      store.address,
      store.city,
      store.zip || store.postcode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const statusVariants = {
    draft: "secondary",
    pending: "warning",
    approved: "info",
    rejected: "danger",
    processed: "success",
    cancelled: "danger",
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Transfer Details - {transfer.transfer_number}
          </DialogTitle>
          <DialogDescription>
            Inter-store stock movement recorded on{" "}
            {new Date(transfer.created_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Transfer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Transfer Number:
                  </span>
                  <span className="font-medium">
                    {transfer.transfer_number}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge
                    variant={statusVariants[transfer.status] || "outline"}
                    className="capitalize"
                  >
                    {transfer.status?.replace("_", " ")}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date Created:</span>
                  <span className="font-medium">
                    {formatDate(transfer.created_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Requested By:</span>
                  <span className="font-medium">
                    {transfer.creator?.username ||
                      transfer.creator?.full_name ||
                      "System"}
                  </span>
                </div>
                {transfer.approver && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Approved By:</span>
                    <span className="font-medium">
                      {transfer.approver.username ||
                        transfer.approver.full_name}
                    </span>
                  </div>
                )}
                {transfer.processor && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Processed By:</span>
                    <span className="font-medium">
                      {transfer.processor.username ||
                        transfer.processor.full_name}
                    </span>
                  </div>
                )}
                {transfer.notes && (
                  <div className="flex justify-between pt-2">
                    <span className="text-muted-foreground">Notes:</span>
                    <span className="font-medium text-right italic text-muted-foreground">
                      {transfer.notes}
                    </span>
                  </div>
                )}
                {transfer.status === "rejected" &&
                  transfer.meta?.rejection_reason && (
                    <div className="pt-3 border-t mt-3">
                      <span className="text-destructive font-semibold block mb-1">
                        Rejection Reason:
                      </span>
                      <span className="text-sm text-destructive italic block">
                        "{transfer.meta.rejection_reason}"
                      </span>
                      {transfer.meta?.rejected_at && (
                        <span className="text-xs text-muted-foreground mt-2 block">
                          Rejected on:{" "}
                          {new Date(transfer.meta.rejected_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Store Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground font-semibold">
                      Source Store:
                    </span>
                    <span className="font-medium">
                      {transfer.source_store?.name || "Unknown"}
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Address:</span>
                    <span className="text-right">
                      {formatAddress(transfer.source_store)}
                    </span>
                  </div>
                </div>
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground font-semibold">
                      Destination Store:
                    </span>
                    <span className="font-medium">
                      {transfer.destination_store?.name || "Unknown"}
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Address:</span>
                    <span className="text-right">
                      {formatAddress(transfer.destination_store)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Transfer Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Tracking</TableHead>
                      <TableHead>Batch/Serial Info</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transfer.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{idx + 1}</TableCell>
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span>{item.product?.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {item.product?.sku}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="capitalize text-[10px]"
                          >
                            {item.product?.tracking_type || "None"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.inventory_batch && (
                            <div className="flex items-center gap-1.5 text-xs">
                              <Layers className="h-3 w-3 text-blue-500" />
                              <span className="font-mono">
                                {item.inventory_batch.batch_number}
                              </span>
                            </div>
                          )}
                          {item.inventory_serial && (
                            <div className="flex items-center gap-1.5 text-[10px] mt-1">
                              <ScanBarcode className="h-3 w-3 text-purple-500" />
                              <span className="font-mono">
                                {item.inventory_serial.serial_number}
                              </span>
                            </div>
                          )}
                          {item.serial_numbers?.length > 0 && (
                            <div className="space-y-1">
                              {item.serial_numbers.slice(0, 2).map((sn, i) => (
                                <div
                                  key={i}
                                  className="flex items-center gap-1.5 text-[10px]"
                                >
                                  <ScanBarcode className="h-3 w-3 text-purple-500" />
                                  <span className="font-mono">{sn}</span>
                                </div>
                              ))}
                              {item.serial_numbers.length > 2 && (
                                <p className="text-[10px] text-muted-foreground">
                                  +{item.serial_numbers.length - 2} more
                                </p>
                              )}
                            </div>
                          )}
                          {!item.inventory_batch &&
                            !item.inventory_serial &&
                            (!item.serial_numbers ||
                              item.serial_numbers.length === 0) && (
                              <span className="text-muted-foreground text-xs italic">
                                -
                              </span>
                            )}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {item.quantity}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!transfer.items?.length && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center h-24 text-muted-foreground"
                        >
                          No items found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Transfer Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Items:</span>
                <span className="font-medium">
                  {transfer.total_items ||
                    transfer.items?.reduce(
                      (acc, item) => acc + item.quantity,
                      0,
                    ) ||
                    0}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground font-semibold">
                  Total Value:
                </span>
                <span className="font-bold text-lg text-primary">
                  {transfer.source_store?.default_currency?.symbol || "Rs."}
                  {parseFloat(transfer.total_value || 0).toFixed(2)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
